// src/components/PaymentResultPopup.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Paper,
  CircularProgress,
  Stack
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import { getDoc, doc } from 'firebase/firestore';
import { db, auth } from '../firebase';

const PaymentResultPopup = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [errorReason, setErrorReason] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('status');
    const errorCode = params.get('error') || '';

    if (result === 'success' || result === 'cancel') {
      setStatus(result);
      setOpen(true);

      if (result === 'success') {
        fetchSubscriptionInfo();
      } else if (result === 'cancel') {
        translateErrorCode(errorCode);
        setLoading(false);
      }
    }
  }, []);

  const translateErrorCode = (code) => {
    const errorMessages = {
      'card_declined': 'Քարտը մերժվել է։ Խնդրում ենք ստուգել քարտի տվյալները և փորձել կրկին։',
      'expired_card': 'Քարտի ժամկետը լրացել է։ Խնդրում ենք օգտագործել այլ քարտ։',
      'insufficient_funds': 'Քարտի վրա բավարար միջոցներ չկան։',
      'processing_error': 'Վճարման ընթացքում տեխնիկական խնդիր է առաջացել։ Խնդրում ենք փորձել կրկին։',
      'authentication_required': 'Անհրաժեշտ է հաստատել ձեր ինքնությունը։ Խնդրում ենք կապվել ձեր բանկի հետ։',
      'default': 'Խնդրում ենք փորձել կրկին կամ կապնվել մեզ հետ։'
    };

    setErrorReason(errorMessages[code] || errorMessages.default);
  };

  const fetchSubscriptionInfo = async () => {
    if (auth.currentUser) {
      try {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setSubscriptionInfo({
            plan: userData.plan,
            renewalDate: userData.next_renewal ? new Date(userData.next_renewal.seconds * 1000) : null,
            remainingSeconds: userData.remaining_seconds,
            remainingTTS: userData.remaining_tts_generations
          });
        }
      } catch (error) {
        console.error("Error fetching subscription info:", error);
      }
    }
    setLoading(false);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0 ժամ 0 րոպե';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} ժամ ${minutes} րոպե`;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('hy-AM', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPlanName = (planCode) => {
    const plans = {
      'Pro_monthly': 'Pro ամսական',
      'Pro_yearly': 'Pro տարեկան',
      'free': 'Անվճար'
    };
    return plans[planCode] || planCode;
  };

  const handleClose = () => {
    setOpen(false);
    // հեռացնում ենք query param, որ dialog նորից չբացվի
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  };

  const handleGoToSettings = () => {
    handleClose();
    navigate('/settings');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <Box
        sx={{
          backgroundColor: status === 'success' ? 'success.light' : 'error.light',
          py: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        {status === 'success' ? (
          <CheckCircleIcon fontSize="large" sx={{ color: 'white' }} />
        ) : (
          <CancelIcon fontSize="large" sx={{ color: 'white' }} />
        )}
        <DialogTitle sx={{ p: 0, color: 'white', fontSize: '1.5rem' }}>
          {status === 'success' ? 'Վճարումը հաջողվեց' : 'Վճարումը ընդհատվեց'}
        </DialogTitle>
      </Box>

      <DialogContent sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : status === 'success' ? (
          <Box>
            <Typography variant="h6" component="div" align="center" sx={{ mb: 2 }}>
              🎉 Շնորհավորում ենք և շնորհակալություն բաժանորդագրվելու համար
            </Typography>

            <Paper elevation={0} sx={{ bgcolor: 'background.default', p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Ձեր բաժանորդագրությունը հաջողությամբ ակտիվացվել է։ Այժմ դուք կարող եք օգտվել մեր բոլոր հնարավորություններից։
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Պլան:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {subscriptionInfo ? getPlanName(subscriptionInfo.plan) : ''}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Մնացած ժամանակ այս ամիս:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {subscriptionInfo ? formatTime(subscriptionInfo.remainingSeconds) : ''}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Տեքստից խոսք կրեդիտներ:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {subscriptionInfo ? subscriptionInfo.remainingTTS : ''}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5 }}>
              <ErrorOutlineIcon color="error" fontSize="large" />
              <Typography variant="h6" component="div">
                Վճարումն ավարտին չհասավ
              </Typography>
            </Box>

            <Typography variant="body1" paragraph>
              {errorReason}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Եթե խնդիրը շարունակվում է, խնդրում ենք կապվել մեզ հետ՝{' '}
              <Box component="span" fontWeight="bold">support@xosum.am</Box>
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        {status === 'success' ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleGoToSettings}
            startIcon={<SettingsIcon />}
            fullWidth
            sx={{ borderRadius: 2, py: 1 }}
          >
            Անցնել կարգավորումներ
          </Button>
        ) : (
          <Button
            onClick={handleClose}
            color="inherit"
            variant="outlined"
            fullWidth
            sx={{ borderRadius: 2, py: 1 }}
          >
            Փակել
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentResultPopup;
